import saveUserColors from 'client/api/userApi'
import Popover, { PopoverProps } from 'client/components/core/Popover'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useManagement from 'client/hooks/useManagement'
import { getCustomColors } from 'client/reducers/managementReducer'
import { setUserColors } from 'client/store/management/managementActions'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ColorCircle } from 'src/editor/shared/ui-lib/color-circle'
import {
  CommonColorPicker,
  CommonColorPickerProps,
} from 'src/editor/shared/ui-lib/color-picker'

export type ColorPickerStandaloneControlProps = Omit<
  CommonColorPickerProps,
  | 'handleChange'
  | 'onChange'
  | 'initialColor'
  | 'userColors'
  | 'onSavedColorsChange'
  | 'onClose'
> & {
  popoverProps?: Partial<PopoverProps>
  color?: CommonColorPickerProps['initialColor']
  update: CommonColorPickerProps['onChange']
  mobileColor?: CommonColorPickerProps['initialColor']
  mobileUpdate?: CommonColorPickerProps['onChange']
}

export const ColorPickerStandaloneControl = ({
  color,
  update,
  mobileColor,
  mobileUpdate,
  popoverProps,
  ...props
}: ColorPickerStandaloneControlProps) => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const { isMobile } = useDeviceModeContext()
  const onChange = isMobile && mobileUpdate ? mobileUpdate : update
  const initialColor = isMobile ? mobileColor || color : color
  const userColors = useManagement(getCustomColors)
  const [haveUserColorsChanged, setHaveUserColorsChanged] = useState(false)

  const handleSavedColorChange = useCallback(
    (colors: string[]) => {
      dispatch(setUserColors(colors))
      setHaveUserColorsChanged(true)
    },
    [dispatch],
  )

  const handleSetIsOpen = useCallback(
    (value: boolean) => {
      if (isOpen && !value && haveUserColorsChanged) {
        saveUserColors(userColors)
        setHaveUserColorsChanged(false)
      }

      setIsOpen(value)
    },
    [isOpen, haveUserColorsChanged, userColors],
  )

  const handleClosePopup = useCallback(() => {
    setIsOpen(false)
    saveUserColors(userColors)
  }, [userColors])

  return (
    <Popover
      ignoredEventTypes={['focusout']}
      isOpen={isOpen}
      setIsOpen={handleSetIsOpen}
      offsetValue={({ placement }) => (placement === 'right' ? 60 : 20)}
      flipOptions={{
        fallbackAxisSideDirection: 'end',
        fallbackPlacements: ['right', 'top-start'],
      }}
      strategyValue="fixed"
      placement="bottom"
      triggerElement={
        <span>
          <ColorCircle color={initialColor} isActive={isOpen} />
        </span>
      }
      popoverContent={
        <CommonColorPicker
          onChange={onChange}
          initialColor={initialColor}
          userColors={userColors}
          onSavedColorsChange={handleSavedColorChange}
          onClose={handleClosePopup}
          {...props}
        />
      }
      {...popoverProps}
    />
  )
}
